import { useEffect, useRef } from "react";
import { supabase } from "@/config/supabaseClient";
import useTripStore from "@/stores/tripBuilder/useTrip";
import usePresenceStore from "@/stores/tripBuilder/usePresence";
import useAuthStore from "@/stores/user/authStore";
import logger from "@/utils/logger";

export const useTripRealtime = (tripId: string) => {
  const user = useAuthStore((state) => state.user);
  const setTripData = useTripStore((state) => state.setTripData);
  const addUser = usePresenceStore((state) => state.addUser);
  const removeUser = usePresenceStore((state) => state.removeUser);

  // Use refs to track subscriptions to avoid dependency changes
  const tripSubscriptionRef = useRef<any>(null);
  const presenceChannelRef = useRef<any>(null);

  useEffect(() => {
    if (!tripId || !user) return;

    // Clean up any existing subscriptions
    if (tripSubscriptionRef.current) {
      tripSubscriptionRef.current.unsubscribe();
    }

    if (presenceChannelRef.current) {
      presenceChannelRef.current.unsubscribe();
    }

    // Subscribe to trip updates
    const tripSubscription = supabase
      .channel(`trip:${tripId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "trips",
          filter: `id=eq.${tripId}`,
        },
        (payload) => {
          if (payload.new) {
            setTripData(payload.new as any);
          }
        }
      )
      .subscribe();

    tripSubscriptionRef.current = tripSubscription;

    // Subscribe to presence updates
    const presenceChannel = supabase.channel(`presence:${tripId}`);
    presenceChannelRef.current = presenceChannel;

    presenceChannel
      .on("presence", { event: "sync" }, () => {
        const state = presenceChannel.presenceState();
        // Convert presence state to array of users
        Object.values(state).forEach((presences) => {
          (presences as any[]).forEach((presence) => {
            addUser({
              user_id: presence.user_id,
              last_seen: presence.last_seen,
              presence_ref: presence.presence_ref,
            });
          });
        });
      })
      .on("presence", { event: "join" }, ({ newPresences }) => {
        newPresences.forEach((presence) => {
          addUser({
            user_id: presence.user_id,
            last_seen: presence.last_seen,
            presence_ref: presence.presence_ref,
          });
        });
      })
      .on("presence", { event: "leave" }, ({ leftPresences }) => {
        leftPresences.forEach((presence) => {
          removeUser(presence.user_id);
        });
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await presenceChannel.track({
            user_id: user.user_id,
            last_seen: new Date().getTime(),
          });
        }
      });

    // Cleanup subscriptions
    return () => {
      if (tripSubscriptionRef.current) {
        tripSubscriptionRef.current.unsubscribe();
      }

      if (presenceChannelRef.current) {
        presenceChannelRef.current.unsubscribe();
      }

      logger.log("Unsubscribed from trip realtime");
    };

    // Only re-subscribe if tripId or user changes
  }, [tripId, user?.user_id]);
};
